import React, { useEffect, useRef, useState } from 'react';
import styles from './Hero.module.css';

const Hero = () => {
  const heroRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const el = heroRef.current;
    if (el) {
      el.classList.add(styles.visible);
    }

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className={styles.hero} ref={heroRef} id="hero">
      {/* Decorative background elements with parallax */}
      <div className={styles.bgDecorations} style={{ transform: `translateY(${scrollY * 0.15}px)` }}>
        <div className={styles.circleOne} />
        <div className={styles.circleTwo} />
        <div className={styles.circleThree} />
        <div className={styles.dotsGrid} />
        <div className={styles.dotsGridSmall} />
        <div className={styles.floatingRing} />
        <div className={styles.diagonalLine} />
      </div>

      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.textBlock}>
            <span className={styles.badge}>
              <span className={styles.badgeDot} />
              Сопровождение ВЭД под ключ
            </span>

            <h1 className={styles.heading}>
              <span className={styles.headingLine}>
                {'Импорт и экспорт'.split(' ').map((w, i) => (
                  <span key={i} className={styles.word} style={{ animationDelay: `${0.25 + i * 0.08}s` }}>{w}&nbsp;</span>
                ))}
              </span>
              <span className={`${styles.headingAccent} ${styles.word}`} style={{ animationDelay: '0.55s' }}>
                без рисков и задержек
              </span>
            </h1>

            <p className={styles.subheading}>
              Берём на себя таможенное оформление, логистику, сертификацию и валютный
              контроль. Вы получаете товар на складе или экспортную выручку на счёте —
              без бюрократии и лишних документов. 20+ лет работы с Европой, Турцией,
              Китаем, Индией и Вьетнамом.
            </p>

            <div className={styles.cta}>
              <button
                className={styles.ctaPrimary}
                onClick={() => scrollToSection('contact-form')}
              >
                Получить консультацию
                <svg
                  className={styles.ctaIcon}
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M4.167 10h11.666M10.833 5l5 5-5 5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                className={styles.ctaOutline}
                onClick={() => scrollToSection('services')}
              >
                Узнать больше
              </button>
            </div>

            <div className={styles.trustRow}>
              <div className={styles.trustItem}>
                <span className={styles.trustValue}>20+</span>
                <span className={styles.trustLabel}>лет опыта</span>
              </div>
              <span className={styles.trustDivider} aria-hidden="true" />
              <div className={styles.trustItem}>
                <span className={styles.trustValue}>6000+</span>
                <span className={styles.trustLabel}>успешных сделок</span>
              </div>
              <span className={styles.trustDivider} aria-hidden="true" />
              <div className={styles.trustItem}>
                <span className={styles.trustValue}>30+</span>
                <span className={styles.trustLabel}>стран-партнёров</span>
              </div>
            </div>

            <div className={styles.contactBadges}>
              <a
                href="mailto:info@konsultant-sovetnik.ru"
                className={styles.contactBadge}
              >
                <svg
                  className={styles.contactIcon}
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <rect
                    x="2"
                    y="4"
                    width="20"
                    height="16"
                    rx="3"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M2 7l8.165 5.715a3 3 0 003.67 0L22 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                info@konsultant-sovetnik.ru
              </a>
              <a href="tel:+79109369889" className={styles.contactBadge}>
                <svg
                  className={styles.contactIcon}
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.362 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0122 16.92z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                +7 (910) 936-98-89
              </a>
            </div>
          </div>

          <div className={styles.illustration} style={{ transform: `translateY(${scrollY * -0.08}px)` }}>
            <div className={styles.illustrationInner}>
              {/* Abstract CSS illustration */}
              <div className={styles.globeOuter}>
                <div className={styles.globeInner}>
                  <div className={styles.globeCore} />
                </div>
                <div className={styles.orbitRing} />
                <div className={styles.orbitRingTwo} />
              </div>

              {/* Floating elements */}
              <div className={styles.floatDoc}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"
                    stroke="#2563EB"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <polyline
                    points="14,2 14,8 20,8"
                    stroke="#2563EB"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <line
                    x1="8"
                    y1="13"
                    x2="16"
                    y2="13"
                    stroke="#2563EB"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <line
                    x1="8"
                    y1="17"
                    x2="12"
                    y2="17"
                    stroke="#2563EB"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>

              <div className={styles.floatScale}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 3v18M3 7l3 5h6L9 7H3zM15 7l3 5h6l-3-5h-6z"
                    stroke="#2563EB"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              <div className={styles.floatShield}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
                    stroke="#2563EB"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <polyline
                    points="9,12 11,14 15,10"
                    stroke="#2563EB"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {/* Dots cluster */}
              <div className={styles.dotsCluster}>
                {Array.from({ length: 9 }).map((_, i) => (
                  <span key={i} className={styles.dot} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
